import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "@/contexts/AuthContext";
import { ReaderCreditProvider } from "@/contexts/ReaderCreditContext";
import { ReaderFunnelProvider } from "@/contexts/ReaderFunnelContext";
import { NotificationProvider } from "@/contexts/NotificationContext";
import { BackendStatusProvider } from "@/contexts/BackendStatusContext";
import { PageTracker } from "@/hooks/usePageTracking";
import ConnectionStatus from "@/components/ConnectionStatus";
import GlobalBackendUnavailableModal from "@/components/GlobalBackendUnavailableModal";
import EnvironmentBanner from "@/components/EnvironmentBanner";

// ============================================================================
// P0 ENVIRONMENT CONFIGURATION - PRODUCTION URL HANDLING
// Uses same origin in production to avoid www/apex domain mismatch issues
// ============================================================================
const ENV_BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const isProduction = window.location.hostname.includes('newaudia.com');

// In production, use same origin (handles both www.newaudia.com and newaudia.com)
// This prevents cross-origin issues when user accesses via www vs apex
const PRODUCTION_BACKEND_URL = isProduction 
  ? `${window.location.protocol}//${window.location.hostname}`
  : '';

// HARDCODE: In production, ALWAYS use same-origin to avoid www/apex mismatch
const BACKEND_URL = isProduction ? PRODUCTION_BACKEND_URL : ENV_BACKEND_URL;

// Export for use throughout the app
export { BACKEND_URL };

// Log backend URL on startup for debugging
console.log(`[ENV] Backend URL: ${BACKEND_URL}`);
console.log(`[ENV] Environment: ${isProduction ? 'PRODUCTION' : 'development/preview'}`);
console.log(`[ENV] Hostname: ${window.location.hostname}`);
if (isProduction) {
  console.log('[ENV] ✓ Production using same-origin backend URL');
}

// Deliverable A: Log backend build identity on startup (once)
(async function logBackendVersion() {
  try {
    const response = await fetch(`${BACKEND_URL}/api/diagnostics/version`);
    if (response.ok) {
      const version = await response.json();
      // Log each field explicitly so it shows in all browsers (not just "Object")
      console.log(`[ENV] Backend Version: git_sha=${version.git_sha} build_id=${version.build_id} env=${version.env} started=${version.startup_time}`);
      console.log('[ENV] Backend Features:', version.features?.join(', ') || 'none');
    } else {
      console.warn('[ENV] Failed to fetch backend version:', response.status);
    }
  } catch (err) {
    console.warn('[ENV] Could not reach backend for version check:', err.message);
  }
})();
// ============================================================================

import NewLandingPage from "@/components/NewLandingPage";
import ReaderConsole from "@/components/ReaderConsole";
import NewAudiaLibraryPage from "@/components/NewAudiaLibraryPage";
import PublicLibraryPage from "@/components/PublicLibraryPage";
import AuthenticatedCatalogPage from "@/components/AuthenticatedCatalogPage";
import CreatorConsole from "@/components/CreatorConsole";
import AdminConsole from "@/components/AdminConsole";
import HomePage from "@/components/HomePage";
import ManuscriptPlayer from "@/components/ManuscriptPlayer";
import AuthPage from "@/components/AuthPage";
import VerifyEmailPage from "@/pages/VerifyEmailPage";
import ReaderHomePage from "@/pages/ReaderHomePage";
import ReaderLibraryPage from "@/pages/ReaderLibraryPage";
import CreatorHomePage from "@/pages/CreatorHomePage";
import CreateAuthorTitlePage from "@/pages/CreateAuthorTitlePage";
import CreatorAnalyticsPage from "@/pages/CreatorAnalyticsPage";
import CreatorAnalyticsDashboard from "@/pages/CreatorAnalyticsDashboard";
import CreatorPromotionsPage from "@/pages/CreatorPromotionsPage";
import AuthorProfileView from "@/components/Creator/AuthorProfileView";
import PublisherHomePage from "@/pages/PublisherHomePage";
import PublisherProfilePage from "@/pages/PublisherProfilePage";
import PublisherAnalyticsPage from "@/pages/PublisherAnalyticsPage";
import ManagedAuthorsPage from "@/pages/ManagedAuthorsPage";
import ManagedTitlesPage from "@/pages/ManagedTitlesPage";
import ManagedAuthorFormPage from "@/pages/ManagedAuthorFormPage";
import ManagedAuthorDetailPage from "@/pages/ManagedAuthorDetailPage";
import ManagedAuthorConsole from "@/pages/ManagedAuthorConsole";
import PublisherManagedAuthorUploadPage from "@/pages/PublisherManagedAuthorUploadPage";
import ManagedAuthorTitlesPage from "@/pages/ManagedAuthorTitlesPage";
import CreateTitlePage from "@/pages/CreateTitlePage";
import EditTitlePage from "@/pages/EditTitlePage";
import AuthorBookDetailsPage from "@/pages/AuthorBookDetailsPage";
import PublicBookPage from "@/pages/PublicBookPage";
// OutlineEditorPage removed - using modal on title page instead
import AdminHomePage from "@/pages/AdminHomePage";
import AdminControlPanelPage from "@/pages/AdminControlPanelPage";
import AdminUserManagementPage from "@/pages/AdminUserManagementPage";
import UserDetailPage from "@/pages/UserDetailPage";
import KioskPage from "@/pages/KioskPage";
import AdminBooksPage from "@/pages/AdminBooksPage";
import AdminAuthorsPage from "@/pages/AdminAuthorsPage";
import AdminPublishersPage from "@/pages/AdminPublishersPage";
import AdminEngagementPage from "@/pages/AdminEngagementPage";
import AdminSystemPage from "@/pages/AdminSystemPage";
import AdminPricingPage from "@/pages/AdminPricingPage";
import AdminCreditsPage from "@/pages/AdminCreditsPage";
import AdminECCPage from "@/pages/AdminECCPage";
import AdminRevenuePage from "@/pages/AdminRevenuePage";
import AdminResetPage from "@/pages/AdminResetPage";
import AdminDiagnosticsPage from "@/pages/AdminDiagnosticsPage";
import AdminImportPage from "@/pages/AdminImportPage";
import AdminMarketingPage from "@/pages/AdminMarketingPage";
import AdminReferralsPage from "@/pages/AdminReferralsPage";
import AdminHousekeepingPage from "@/pages/AdminHousekeepingPage";
import AdminKioskPage from "@/pages/AdminKioskPage";
import AdminActivationTrackerPage from "@/pages/AdminActivationTrackerPage";
import TTSDiagnosticsPage from "@/pages/TTSDiagnosticsPage";
import AccountPage from "@/pages/AccountPage";
import AccountTransactionsPage from "@/pages/AccountTransactionsPage";
import PaymentSuccessPage from "@/pages/PaymentSuccessPage";
import PaymentCancelPage from "@/pages/PaymentCancelPage";
import AboutPage from "@/pages/AboutPage";
import PricingPage from "@/pages/PricingPage";
import FAQPage from "@/pages/FAQPage";
import ContactPage from "@/pages/ContactPage";
import AuthorContactPage from "@/pages/AuthorContactPage";
import PublisherContactPage from "@/pages/PublisherContactPage";
import HelpCenterPage from "@/pages/HelpCenterPage";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import TermsOfServicePage from "@/pages/TermsOfServicePage";
import AcceptableUsePage from "@/pages/AcceptableUsePage";
import AIUsagePolicyPage from "@/pages/AIUsagePolicyPage";
import AuthorPublisherAddendumPage from "@/pages/AuthorPublisherAddendumPage";
import ReaderCreditBillingPolicyPage from "@/pages/ReaderCreditBillingPolicyPage";
import SubscriptionPage from "@/pages/SubscriptionPage";
// AI Discovery Pages (public, crawlable)
import AIDiscoveryPage from "@/pages/AIDiscoveryPage";
import ForAuthorsPage from "@/pages/ForAuthorsPage";
import ForPublishersPage from "@/pages/ForPublishersPage";
import HowItWorksPage from "@/pages/HowItWorksPage";
import WhyNewAudiaPage from "@/pages/WhyNewAudiaPage";
import OurAuthorsPage from "@/pages/OurAuthorsPage";
import PublicSamplePage from "@/pages/PublicSamplePage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
// New 3-Surface Account Architecture
import ProfilePage from "@/pages/account/ProfilePage";
import CreditsPage from "@/pages/account/CreditsPage";
import BillingPage from "@/pages/account/BillingPage";
import ProtectedRoute from "@/components/ProtectedRoute";
import MainNav from "@/components/MainNav";
import ScrollToTop from "@/components/ScrollToTop";
import { Toaster } from "@/components/ui/sonner";
import NavigationModeDetector from "@/navigation/NavigationModeDetector";
// Reader Funnel Modals
import RegistrationGateModal from "@/components/RegistrationGateModal";
import CreditExpiredModal from "@/components/CreditExpiredModal";
import LowCreditModal from "@/components/LowCreditModal";
import UrgentCreditModal from "@/components/UrgentCreditModal";
import PricingConversionModal from "@/components/PricingConversionModal";
import SimpleIngestGate7Page from "@/components/SimpleIngestGate7Page";
import OnboardingModal from "@/components/OnboardingModal";

// Redirect component for old outline route
const OutlineRedirect = () => {
  const { useParams, useNavigate } = require('react-router-dom');
  const { manuscriptId } = useParams();
  const navigate = useNavigate();
  React.useEffect(() => {
    navigate(`/author/titles/${manuscriptId}?outline=1`, { replace: true });
  }, [manuscriptId, navigate]);
  return null;
};

// Redirect component for publisher title view -> edit
const PublisherTitleRedirect = () => {
  const { useParams, useNavigate } = require('react-router-dom');
  const { titleId } = useParams();
  const navigate = useNavigate();
  React.useEffect(() => {
    navigate(`/publisher/titles/${titleId}/edit`, { replace: true });
  }, [titleId, navigate]);
  return null;
};

function App() {
  return (
    <HelmetProvider>
    <div className="App">
      {/* P0 ENVIRONMENT INTEGRITY: Always show environment banner */}
      <EnvironmentBanner />
      <BackendStatusProvider>
      <AuthProvider>
        <ReaderCreditProvider>
        <ReaderFunnelProvider>
        <NotificationProvider>
        <BrowserRouter>
          <ScrollToTop />
          <PageTracker />
          <GlobalBackendUnavailableModal />
          <NavigationModeDetector>
            <Routes>
            {/* Public routes */}
            <Route path="/" element={<NewLandingPage />} />
            <Route path="/login" element={<AuthPage />} />
            <Route path="/register" element={<AuthPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />

            {/* Kiosk Module — admin-gated event lead capture (iPad-optimized) */}
            <Route path="/kiosk" element={<KioskPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/contact/authors" element={<AuthorContactPage />} />
            <Route path="/contact/publishers" element={<PublisherContactPage />} />
            <Route path="/authors" element={<OurAuthorsPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsOfServicePage />} />
            <Route path="/acceptable-use" element={<AcceptableUsePage />} />
            <Route path="/ai-policy" element={<AIUsagePolicyPage />} />
            <Route path="/author-publisher-addendum" element={<AuthorPublisherAddendumPage />} />
            <Route path="/reader-credits-billing" element={<ReaderCreditBillingPolicyPage />} />
            <Route path="/public/library" element={<PublicLibraryPage />} />
            
            {/* Public Sample Sharing - for social media links */}
            <Route path="/sample/:manuscriptId" element={<PublicSamplePage />} />
            
            {/* Public Book Page - SEO Phase 3, crawlable */}
            <Route path="/book/:slug" element={<PublicBookPage />} />
            
            {/* AI Discovery Pages - Public, crawlable for LLMs and search engines */}
            <Route path="/ai" element={<AIDiscoveryPage />} />
            <Route path="/for-authors" element={<ForAuthorsPage />} />
            <Route path="/for-publishers" element={<ForPublishersPage />} />
            <Route path="/how-it-works" element={<HowItWorksPage />} />
            <Route path="/why-newaudia" element={<WhyNewAudiaPage />} />
            
            {/* Help Center - Protected, role-aware content */}
            <Route
              path="/help"
              element={
                <ProtectedRoute>
                  <HelpCenterPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/help/:sectionId"
              element={
                <ProtectedRoute>
                  <HelpCenterPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/help/:sectionId/:articleId"
              element={
                <ProtectedRoute>
                  <HelpCenterPage />
                </ProtectedRoute>
              }
            />
            
            {/* Protected Reader Home (new logged-in homepage) */}
            <Route
              path="/home"
              element={
                <ProtectedRoute>
                  <ReaderHomePage />
                </ProtectedRoute>
              }
            />

            {/* Protected NewAudia Library (Full Catalog for Authenticated Users) */}
            <Route
              path="/library"
              element={
                <ProtectedRoute requiredRole="reader">
                  <AuthenticatedCatalogPage />
                </ProtectedRoute>
              }
            />

            {/* Protected My Library (User's Personal Collection with Play/Archive buttons) */}
            <Route
              path="/reader/my-library"
              element={
                <ProtectedRoute requiredRole="reader">
                  <ReaderLibraryPage />
                </ProtectedRoute>
              }
            />

            
            {/* Protected Author Home (canonical) */}
            <Route
              path="/author"
              element={
                <ProtectedRoute requiredRole="author">
                  <CreatorHomePage />
                </ProtectedRoute>
              }
            />
            
            {/* Author Title Creation - Title-first upload flow */}
            <Route
              path="/author/titles/new"
              element={
                <ProtectedRoute requiredRole="author">
                  <CreateAuthorTitlePage />
                </ProtectedRoute>
              }
            />
            
            {/* Legacy Creator route - redirect to Author */}
            <Route
              path="/creator"
              element={
                <ProtectedRoute requiredRole="author">
                  <CreatorHomePage />
                </ProtectedRoute>
              }
            />
            
            {/* Protected Publisher Home */}
            <Route
              path="/publisher"
              element={
                <ProtectedRoute requiredRole="publisher">
                  <PublisherHomePage />
                </ProtectedRoute>
              }
            />
            
            {/* Protected Publisher Profile */}
            <Route
              path="/publisher/profile"
              element={
                <ProtectedRoute requiredRole="publisher">
                  <PublisherProfilePage />
                </ProtectedRoute>
              }
            />
            
            {/* Protected Publisher Analytics */}
            <Route
              path="/publisher/analytics"
              element={
                <ProtectedRoute requiredRole="publisher">
                  <PublisherAnalyticsPage />
                </ProtectedRoute>
              }
            />
            
            {/* Protected Publisher Managed Authors List */}
            <Route
              path="/publisher/authors"
              element={
                <ProtectedRoute requiredRole="publisher">
                  <ManagedAuthorsPage />
                </ProtectedRoute>
              }
            />
            
            {/* Protected Publisher Managed Titles List */}
            <Route
              path="/publisher/titles"
              element={
                <ProtectedRoute requiredRole="publisher">
                  <ManagedTitlesPage />
                </ProtectedRoute>
              }
            />
            
            {/* Protected Publisher Add New Author */}
            <Route
              path="/publisher/authors/new"
              element={
                <ProtectedRoute requiredRole="publisher">
                  <ManagedAuthorFormPage />
                </ProtectedRoute>
              }
            />
            
            {/* Protected Publisher Edit Author */}
            <Route
              path="/publisher/authors/:authorId/edit"
              element={
                <ProtectedRoute requiredRole="publisher">
                  <ManagedAuthorFormPage />
                </ProtectedRoute>
              }
            />
            
            {/* Protected Publisher Author Detail (Titles List) */}
            <Route
              path="/publisher/authors/:authorId"
              element={
                <ProtectedRoute requiredRole="publisher">
                  <ManagedAuthorDetailPage />
                </ProtectedRoute>
              }
            />
            
            {/* Protected Publisher Managed Author Console */}
            <Route
              path="/publisher/authors/:authorId/console"
              element={
                <ProtectedRoute requiredRole="publisher">
                  <ManagedAuthorConsole />
                </ProtectedRoute>
              }
            />
            
            {/* Protected Publisher Upload New Manuscript (repurposed from Create Title) */}
            <Route
              path="/publisher/authors/:authorId/titles/new"
              element={
                <ProtectedRoute requiredRole="publisher">
                  <CreateTitlePage />
                </ProtectedRoute>
              }
            />
            
            {/* Publisher Simple Ingestion Upload Page for Managed Authors */}
            <Route
              path="/publisher/authors/:authorId/upload"
              element={
                <ProtectedRoute requiredRole="publisher">
                  <PublisherManagedAuthorUploadPage />
                </ProtectedRoute>
              }
            />
            
            {/* Publisher Managed Author Titles List Page */}
            <Route
              path="/publisher/authors/:authorId/titles"
              element={
                <ProtectedRoute requiredRole="publisher">
                  <ManagedAuthorTitlesPage />
                </ProtectedRoute>
              }
            />
            
            {/* Protected Publisher View Title - redirect to edit page */}
            <Route
              path="/publisher/titles/:titleId"
              element={
                <ProtectedRoute requiredRole="publisher">
                  <PublisherTitleRedirect />
                </ProtectedRoute>
              }
            />
            
            {/* Protected Publisher Edit Title */}
            <Route
              path="/publisher/titles/:titleId/edit"
              element={
                <ProtectedRoute requiredRole="publisher">
                  <EditTitlePage />
                </ProtectedRoute>
              }
            />
            
            {/* P0 DIAGNOSTICS: TTS Diagnostics Page (Publisher/Admin) */}
            <Route
              path="/publisher/diagnostics"
              element={
                <ProtectedRoute requiredRole="publisher">
                  <TTSDiagnosticsPage />
                </ProtectedRoute>
              }
            />
            
            {/* Protected Admin Control Panel (new default landing) */}
            <Route
              path="/admin"
              element={
                <ProtectedRoute requiredRole="admin">
                  <AdminControlPanelPage />
                </ProtectedRoute>
              }
            />
            
            {/* Admin Dashboard (legacy path, now at /admin/dashboard) */}
            <Route
              path="/admin/dashboard"
              element={
                <ProtectedRoute requiredRole="admin">
                  <AdminHomePage />
                </ProtectedRoute>
              }
            />
            
            {/* Protected Admin User Management */}
            <Route
              path="/admin/users"
              element={
                <ProtectedRoute requireRole="admin">
                  <AdminUserManagementPage />
                </ProtectedRoute>
              }
            />

            {/* Protected Admin User Detail */}
            <Route
              path="/admin/users/:userId"
              element={
                <ProtectedRoute requireRole="admin">
                  <UserDetailPage />
                </ProtectedRoute>
              }
            />

            {/* Protected Admin Books */}
            <Route
              path="/admin/books"
              element={
                <ProtectedRoute requiredRole="admin">
                  <AdminBooksPage />
                </ProtectedRoute>
              }
            />

            {/* Protected Admin Creators */}
            <Route
              path="/admin/creators"
              element={
                <ProtectedRoute requiredRole="admin">
                  <AdminAuthorsPage />
                </ProtectedRoute>
              }
            />

            {/* Admin Authors - New Route */}
            <Route
              path="/admin/authors"
              element={
                <ProtectedRoute requiredRole="admin">
                  <AdminAuthorsPage />
                </ProtectedRoute>
              }
            />

            {/* Protected Admin Publishers */}
            <Route
              path="/admin/publishers"
              element={
                <ProtectedRoute requiredRole="admin">
                  <AdminPublishersPage />
                </ProtectedRoute>
              }
            />

            {/* Admin Engagement Analytics Console */}
            <Route
              path="/admin/engagement"
              element={
                <ProtectedRoute requiredRole="admin">
                  <AdminEngagementPage />
                </ProtectedRoute>
              }
            />

            {/* Protected Admin System */}
            <Route
              path="/admin/system"
              element={
                <ProtectedRoute requiredRole="admin">
                  <AdminSystemPage />
                </ProtectedRoute>
              }
            />
            {/* Admin Economic Reset Tool */}
            <Route
              path="/admin/system/reset"
              element={
                <ProtectedRoute requiredRole="admin">
                  <AdminResetPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/pricing"
              element={
                <ProtectedRoute requiredRole="admin">
                  <AdminPricingPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/credits"
              element={
                <ProtectedRoute requiredRole="admin">
                  <AdminCreditsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/economics"
              element={
                <ProtectedRoute requiredRole="admin">
                  <AdminECCPage />
                </ProtectedRoute>
              }
            />
            {/* Alias for ECC page */}
            <Route
              path="/admin/ecc"
              element={
                <ProtectedRoute requiredRole="admin">
                  <AdminECCPage />
                </ProtectedRoute>
              }
            />
            {/* P1 Revenue Governance Dashboard */}
            <Route
              path="/admin/revenue"
              element={
                <ProtectedRoute requiredRole="admin">
                  <AdminRevenuePage />
                </ProtectedRoute>
              }
            />
            
            {/* P0 Production Diagnostics - Admin Only */}
            <Route
              path="/admin/diagnostics"
              element={
                <ProtectedRoute requiredRole="admin">
                  <AdminDiagnosticsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/import"
              element={
                <ProtectedRoute requiredRole="admin">
                  <AdminImportPage />
                </ProtectedRoute>
              }
            />
            {/* Admin Marketing Page - Re-engagement Tools */}
            <Route
              path="/admin/marketing"
              element={
                <ProtectedRoute requiredRole="admin">
                  <AdminMarketingPage />
                </ProtectedRoute>
              }
            />
            {/* Admin Referrals Page */}
            <Route
              path="/admin/referrals"
              element={
                <ProtectedRoute requiredRole="admin">
                  <AdminReferralsPage />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/admin/housekeeping"
              element={
                <ProtectedRoute requiredRole="admin">
                  <AdminHousekeepingPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/kiosk"
              element={
                <ProtectedRoute requiredRole="admin">
                  <AdminKioskPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/activation-tracker"
              element={
                <ProtectedRoute requiredRole="admin">
                  <AdminActivationTrackerPage />
                </ProtectedRoute>
              }
            />
            
            {/* ============================================== */}
            {/* 3-SURFACE ACCOUNT ARCHITECTURE (P1.0)         */}
            {/* Canonical Routes:                              */}
            {/*   /account/profile - Identity & Payment        */}
            {/*   /account/credits - Credits Ledger            */}
            {/*   /account/billing - Financial Transactions    */}
            {/* ============================================== */}
            
            {/* Canonical: /account redirects to /account/profile */}
            <Route
              path="/account"
              element={
                <ProtectedRoute>
                  <Navigate to="/account/profile" replace />
                </ProtectedRoute>
              }
            />
            
            {/* Reader Profile (Identity + Payment + Subscription Mgmt) */}
            <Route
              path="/account/profile"
              element={
                <ProtectedRoute>
                  <ProfilePage />
                </ProtectedRoute>
              }
            />
            
            {/* Credits Ledger (Daily Aggregated) */}
            <Route
              path="/account/credits"
              element={
                <ProtectedRoute>
                  <CreditsPage />
                </ProtectedRoute>
              }
            />
            
            {/* Billing Summary (FTR-backed + Receipt Resend) */}
            <Route
              path="/account/billing"
              element={
                <ProtectedRoute>
                  <BillingPage />
                </ProtectedRoute>
              }
            />
            
            {/* Backward compatibility: /account/transactions -> /account/billing */}
            <Route
              path="/account/transactions"
              element={
                <Navigate to="/account/billing" replace />
              }
            />
            
            {/* Legacy: /account/subscription merged into profile */}
            <Route
              path="/account/subscription"
              element={
                <ProtectedRoute>
                  <Navigate to="/account/profile" replace />
                </ProtectedRoute>
              }
            />
            
            {/* Payment Result Pages */}
            <Route
              path="/payment/success"
              element={<PaymentSuccessPage />}
            />
            <Route
              path="/payment/cancel"
              element={<PaymentCancelPage />}
            />
            
            {/* Console routes (protected) */}
            {/* Legacy /reader-console - redirect to /home */}
            <Route
              path="/reader-console"
              element={
                <ProtectedRoute>
                  <Navigate to="/home" replace />
                </ProtectedRoute>
              }
            />
            {/* Author Console (canonical) */}
            <Route
              path="/author-console"
              element={
                <ProtectedRoute requiredRole="author">
                  <CreatorConsole />
                </ProtectedRoute>
              }
            />
            {/* Legacy Creator Console - alias */}
            <Route
              path="/creator-console"
              element={
                <ProtectedRoute requiredRole="author">
                  <CreatorConsole />
                </ProtectedRoute>
              }
            />
            {/* Simple Ingestion Preview Page - Gate 7 */}
            <Route
              path="/simple-ingest/preview/:jobId"
              element={
                <ProtectedRoute requiredRole="author">
                  <SimpleIngestGate7Page />
                </ProtectedRoute>
              }
            />
            {/* Author Book Details Page (new) */}
            <Route
              path="/author/titles/:manuscriptId"
              element={
                <ProtectedRoute requiredRole="author">
                  <AuthorBookDetailsPage />
                </ProtectedRoute>
              }
            />
            {/* Author Outline Editor - Redirect to title page with modal query param */}
            <Route
              path="/author/titles/:manuscriptId/outline"
              element={
                <ProtectedRoute requiredRole="author">
                  <OutlineRedirect />
                </ProtectedRoute>
              }
            />
            {/* Author Analytics (canonical) */}
            <Route
              path="/author-analytics/:manuscriptId"
              element={
                <ProtectedRoute requiredRole="author">
                  <CreatorAnalyticsPage />
                </ProtectedRoute>
              }
            />
            {/* Legacy Creator Analytics - alias */}
            <Route
              path="/creator-analytics/:manuscriptId"
              element={
                <ProtectedRoute requiredRole="author">
                  <CreatorAnalyticsPage />
                </ProtectedRoute>
              }
            />
            {/* Author Analytics Dashboard (canonical) */}
            <Route
              path="/author/analytics"
              element={
                <ProtectedRoute requiredRole="author">
                  <CreatorAnalyticsDashboard />
                </ProtectedRoute>
              }
            />
            {/* Legacy - alias */}
            <Route
              path="/creator/analytics"
              element={
                <ProtectedRoute requiredRole="author">
                  <CreatorAnalyticsDashboard />
                </ProtectedRoute>
              }
            />
            {/* Author Promotions (canonical) */}
            <Route
              path="/author/promotions"
              element={
                <ProtectedRoute requiredRole="author">
                  <CreatorPromotionsPage />
                </ProtectedRoute>
              }
            />
            {/* Legacy - alias */}
            <Route
              path="/creator/promotions"
              element={
                <ProtectedRoute requiredRole="author">
                  <CreatorPromotionsPage />
                </ProtectedRoute>
              }
            />
            {/* Author Profile View (canonical) */}
            <Route
              path="/author/author-profile/view"
              element={
                <ProtectedRoute requiredRole="author">
                  <AuthorProfileView />
                </ProtectedRoute>
              }
            />
            {/* Legacy - alias */}
            <Route
              path="/creator/author-profile/view"
              element={
                <ProtectedRoute requiredRole="author">
                  <AuthorProfileView />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin-console"
              element={
                <ProtectedRoute requiredRole="admin">
                  <AdminConsole />
                </ProtectedRoute>
              }
            />
            
            {/* Other routes */}
            <Route path="/app" element={<HomePage />} />
            <Route path="/player/:sessionId" element={<ManuscriptPlayer />} />
            </Routes>
            
            {/* Reader Funnel Global Modals */}
            <RegistrationGateModal />
            <CreditExpiredModal />
            <LowCreditModal />
            <UrgentCreditModal />
            <PricingConversionModal />
            <OnboardingModal />
          </NavigationModeDetector>
        </BrowserRouter>
        <Toaster />
        <ConnectionStatus />
        </NotificationProvider>
        </ReaderFunnelProvider>
        </ReaderCreditProvider>
      </AuthProvider>
      </BackendStatusProvider>
    </div>
    </HelmetProvider>
  );
}

export default App;
